import React, { useEffect, useRef } from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  useLocation,
  Navigate,
  useNavigate,
  useParams,
} from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import Navbar from "./components/layout/Navbar";
import SidebarNavbar from "./components/layout/SidebarNavbar";
// Landing Page Components
import LandingNavbar from "./components/landing/LandingNavbar";
import HeroSectionNew from "./components/landing/HeroSectionNew";
import DeviceMockup from "./components/landing/DeviceMockup";
import TrustedBySection from "./components/landing/TrustedBySection";
import FeatureShowcase from "./components/landing/FeatureShowcase";
import PapersShowcase from "./components/landing/PapersShowcase";
import PlatformShowcase from "./components/landing/PlatformShowcase";
import CollaboratorsShowcase from "./components/landing/CollaboratorsShowcase";
import ReviewsShowcase from "./components/landing/ReviewsShowcase";
import StatsSection from "./components/landing/StatsSection";
import BottomCTA from "./components/landing/BottomCTA";
import LandingFooter from "./components/landing/LandingFooter";
import FeaturesPage from "./components/landing/FeaturesPage";
import { DocumentationPage, ResearchGuidesPage, BlogPage, BlogDetailPage, GuideDetailPage, HelpCenterPage, ResourcesPage } from "./components/landing/resources";
import { AboutPage, CareersPage, ContactPage, CompanyPage } from "./components/landing/company";
import PrivacyPolicyPage from "./components/legal/PrivacyPolicyPage";
import TermsOfServicePage from "./components/legal/TermsOfServicePage";
import CookiePolicyPage from "./components/legal/CookiePolicyPage";
import SignIn from "./components/Auth/SignIn";
import SignUp from "./components/Auth/SignUp";

// ✅ Import workshop test utilities for debugging (development only)
import "./utils/workshopTestUtils";
import EmailVerification from "./components/Auth/EmailVerification";
import ForgotPassword from "./components/Auth/ForgotPassword";
import ResetPassword from "./components/Auth/ResetPassword";
import Onboarding from "./components/Auth/Onboarding";
// Research Rooms - NEW implementation
import { RoomDetail, RoomPreviewPage } from "./components/rooms";
import { RoomProvider } from "./context/RoomContext";
import { RoomMessagesProvider } from "./context/RoomMessagesContext";
import { RoomFilesProvider } from "./context/RoomFilesContext";
import { RoomTasksProvider } from "./context/RoomTasksContext";
import { RoomCalendarProvider } from "./context/RoomCalendarContext";
import RoomChatPlaceholder from "./components/placeholders/RoomChatPlaceholder";
import PaperDetail from "./components/explore/PaperDetail";
import CollaboratorsLayout from "./components/collaborators/CollaboratorsLayout";
import PersistentCollaboratorsLayout from "./components/collaborators/PersistentCollaboratorsLayout";
import FreelanceLayout from "./components/freelance/FreelanceLayout";
import MessagingCenter from "./components/messaging/MessagingCenter";
import { SettingsPage } from "./components/settings";
import ProfessionalProfilePage from "./components/profile/ProfessionalProfilePage";
import CommunityPreview from "./components/communities/CommunityPreview";
import CommunityDashboard from "./components/communities/CommunityDashboard";
import CoursesRoutes from "./components/courses/CoursesRoutes";
// WorkshopsRoutes replaced by PersistentWorkshopsLayout for scroll persistence
import PostDetailPage from "./components/feed/PostDetailPage";
import TrendingTopicsPage from "./components/feed/TrendingTopicsPage";
import UnansweredQuestionsPage from "./components/feed/UnansweredQuestionsPage";
import TopicPage from "./components/feed/TopicPage";
import DraftsPage from "./components/feed/DraftsPage";
import BrowseOpportunities from "./components/feed/BrowseOpportunities";
import BountyQuestionsPage from "./components/feed/BountyQuestionsPage";
import PersistentFeedLayout from "./components/layout/PersistentFeedLayout";
import PersistentExploreLayout from "./components/layout/PersistentExploreLayout";
import PersistentRoomsLayout from "./components/layout/PersistentRoomsLayout";
import PersistentCommunitiesLayout from "./components/layout/PersistentCommunitiesLayout";
import PersistentWorkshopsLayout from "./components/layout/PersistentWorkshopsLayout";
// PersistentCommunityDashboardLayout no longer needed - all views handled directly in PersistentCommunitiesLayout

import { UserProvider, useUserContext } from "./context/UserContext";
import { NotificationProvider } from "./context/NotificationContext";
import {
  saveCurrentRoute,
  getLastRoute,
  shouldRedirectToSavedRoute,
} from "./utils/routePersistence";
import "./App.css";

// Create QueryClient instance for React Query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 5 * 60 * 1000, // 5 minutes default
    },
  },
});

// ============================================
// DISABLE BROWSER SCROLL RESTORATION
// ============================================
// This is CRITICAL for Twitter/Reddit-style navigation.
// Without this, the browser auto-restores scroll position
// AFTER React renders, causing visible scroll movement.
// With 'manual', scroll position is preserved in the DOM
// because PersistentFeedLayout never unmounts the feed.
if ('scrollRestoration' in window.history) {
  window.history.scrollRestoration = 'manual';
}

// Workshop Components - REMOVED (backend changed)

// Home Redirect Component - Handles routing based on auth status and onboarding
const HomeRedirect = () => {
  const { isAuthenticated, user, loading } = useUserContext();

  // Show loading screen while checking auth status
  if (loading) {
    return <div className="loading-screen">Loading...</div>;
  }
  // If user is authenticated
  if (isAuthenticated) {
    // Check if user needs onboarding
    if (user && !user.onboardingCompleted) {
      return <Navigate to="/onboarding" replace />;
    }
    // User is authenticated and has completed onboarding
    // Redirect to /feed-home which renders PersistentFeedLayout
    return <Navigate to="/feed-home" replace />;
  }

  // User is not authenticated, show landing page
  return (
    <>
      <LandingNavbar />
      <HeroSectionNew />
      <DeviceMockup />
      <TrustedBySection />
      <StatsSection />
      <FeatureShowcase />
      <PapersShowcase />
      <PlatformShowcase />
      <CollaboratorsShowcase />
      <ReviewsShowcase />
      <BottomCTA />
      <LandingFooter />
    </>
  );
};

// Auth Route Component - Redirects authenticated users based on onboarding status
const AuthRoute = ({ children }) => {
  const { isAuthenticated, user, loading } = useUserContext();
  const location = useLocation();

  // Show loading screen while checking auth status
  if (loading) {
    return <div className="loading-screen">Loading...</div>;
  }

  // If user is already authenticated
  if (isAuthenticated) {
    // Check if user needs onboarding
    if (user && !user.onboardingCompleted) {
      // If we're already on the onboarding page, show it
      if (location.pathname === "/onboarding") {
        return children;
      }
      // Otherwise redirect to onboarding
      return <Navigate to="/onboarding" replace />;
    }
    // User is authenticated and has completed onboarding, redirect to home (feed)
    return <Navigate to="/" replace />;
  }

  // Special case for onboarding - redirect to signin if not authenticated
  if (location.pathname === "/onboarding") {
    return <Navigate to="/signin" state={{ from: location.pathname }} />;
  }

  // User is not authenticated, show the requested auth page
  return children;
};

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, user, loading } = useUserContext();
  const location = useLocation();

  // Show nothing while authentication is loading
  if (loading) {
    return <div className="loading-screen">Loading...</div>;
  }

  // If not authenticated, redirect to signin
  if (!isAuthenticated) {
    return <Navigate to="/signin" state={{ from: location.pathname }} />;
  }

  // If authenticated but needs onboarding, redirect to onboarding
  if (user && !user.onboardingCompleted) {
    return <Navigate to="/onboarding" replace />;
  }

  // User is authenticated and has completed onboarding, show protected content
  return children;
};

// Redirect component for old /rooms URLs with params
const RoomsToLabsRedirect = ({ preview = false }) => {
  const { roomId } = useParams();
  const targetPath = preview ? `/labs/${roomId}/preview` : `/labs/${roomId}`;
  return <Navigate to={targetPath} replace />;
};

function AppLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, loading, fetchUserProfile } = useUserContext();
  const isResearchRooms =
    location.pathname.startsWith("/labs") ||
    location.pathname.startsWith("/fields");
  // Check if we're in a specific room (not the labs list, my-labs, or preview page)
  // Must be /labs/{mongoId} format - exclude /labs, /labs/, /labs/my-labs, and /labs/{id}/preview
  const isRoomDetail = /^\/labs\/[a-f0-9]{24}(\/(?!preview)[^/]*)?$/.test(location.pathname);
  const isChatPage = location.pathname.startsWith("/chat");
  const isExplorePapers = location.pathname.startsWith("/explore");
  const isResearchFeed =
    location.pathname === "/" || location.pathname.startsWith("/feed");
  const isSinglePostView =
    location.pathname.startsWith("/post/") ||
    location.pathname.startsWith("/posts/");
  const isTopicsPage = location.pathname.startsWith("/topics");
  const isQuestionsPage = location.pathname.startsWith("/questions");
  const isDraftsPage = location.pathname.startsWith("/drafts");
  const isCollaborators = location.pathname.startsWith("/collaborators");
  const isCollaborationsPage = location.pathname.startsWith("/collaborations");
  const isFreelance = location.pathname.startsWith("/freelance");
  const isMessaging = location.pathname.startsWith("/messages");
  const isSettings = location.pathname.startsWith("/settings");
  const isProfile = location.pathname.startsWith("/profile");
  // Workshops removed - backend changed
  const isCourses = location.pathname.toLowerCase().startsWith("/courses");
  const isWorkshops = location.pathname.toLowerCase().startsWith("/workshops");
  const isCommunities = location.pathname.startsWith("/communities");
  const isLandingPage = location.pathname === "/" && !isAuthenticated;
  const isResourcePage = ["/resources", "/docs", "/guides", "/blog", "/help", "/about", "/careers", "/contact", "/features", "/company"].includes(location.pathname) || location.pathname.startsWith('/blog/') || location.pathname.startsWith('/docs/') || location.pathname.startsWith('/guides/');

  // Updated logic: Show sidebar for explore page regardless of authentication status
  // Settings page has its own sidebar, so exclude it from main sidebar layout
  const showSidebar =
    isExplorePapers ||
    (isAuthenticated &&
      !isSettings && // Settings has its own full-page layout with built-in sidebar
      (isResearchRooms ||
        isResearchFeed ||
        isSinglePostView ||
        isTopicsPage ||
        isQuestionsPage ||
        isDraftsPage ||
        isCollaborators ||
        isCollaborationsPage ||
        isFreelance ||
        isMessaging ||
        isProfile ||
        isCourses ||
        isWorkshops ||
        isCommunities));

  // Save current route on navigation and check for redirect on load
  useEffect(() => {
    // Don't save routes for auth pages
    const isAuthPage = ["/signin", "/signup", "/onboarding"].includes(
      location.pathname
    );

    // Save the current route if authenticated and not on auth pages
    if (!isAuthPage && isAuthenticated) {
      saveCurrentRoute(location.pathname);
}

    // Handle page refresh/navigation (detected via performance navigation or referrer)
    const isPageRefresh =
      window.performance &&
      (window.performance.navigation.type === 1 || document.referrer === "");

    if (isAuthenticated && isPageRefresh) {
      // If we're refreshing an authenticated page, check if we should redirect
      if (shouldRedirectToSavedRoute(location.pathname)) {
        const lastRoute = getLastRoute();
        if (lastRoute) {
navigate(lastRoute, { replace: true });
        }
      }
    }
  }, [location.pathname, isAuthenticated, navigate]);

  // Fetch user profile data when the app loads and user is authenticated
  const profileFetchedRef = useRef(false);

  useEffect(() => {
    if (isAuthenticated && !loading && !profileFetchedRef.current) {
      profileFetchedRef.current = true;
      fetchUserProfile();
    }

    return () => {
      // Reset on unmount (though this shouldn't happen for AppLayout)
      // profileFetchedRef.current = false;
    };
  }, [isAuthenticated, loading, fetchUserProfile]);

  // Check if we're on auth pages (they have their own LandingNavbar via AuthLayout)
  const isAuthPage = ["/signin", "/signup", "/onboarding", "/forgot-password", "/reset-password", "/email-verification"].includes(location.pathname);

  return (
    <div className="App">
      {/* Settings page has its own full-page layout, auth pages have LandingNavbar via AuthLayout */}
      {!isSettings && !isAuthPage && !isLandingPage && !isResourcePage && (showSidebar ? <SidebarNavbar /> : <Navbar />)}
      <main
        className={`${showSidebar ? "research-rooms-sidebar-layout" : ""}${
          isRoomDetail ? " room-detail-active" : ""
        }${isSettings ? " stg-main-fullpage" : ""}`}
      >
        <Routes>
          {/* Home route with conditional content */}
          <Route path="/" element={<HomeRedirect />} />

          {/* Auth routes with conditional redirects */}
          <Route
            path="/signin"
            element={
              <AuthRoute>
                <SignIn />
              </AuthRoute>
            }
          />
          <Route
            path="/signup"
            element={
              <AuthRoute>
                <SignUp />
              </AuthRoute>
            }
          />
          <Route path="/email-verification" element={<EmailVerification />} />
          <Route
            path="/onboarding"
            element={
              <AuthRoute>
                <Onboarding />
              </AuthRoute>
            }
          />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password/:token" element={<ResetPassword />} />

          {/* Resource Pages — Public, no auth required */}
          <Route path="/resources" element={<ResourcesPage />} />
          <Route path="/docs" element={<DocumentationPage />} />
          <Route path="/docs/:slug" element={<GuideDetailPage type="doc" />} />
          <Route path="/guides" element={<ResearchGuidesPage />} />
          <Route path="/guides/:slug" element={<GuideDetailPage type="guide" />} />
          <Route path="/blog" element={<BlogPage />} />
          <Route path="/blog/:slug" element={<BlogDetailPage />} />
          <Route path="/help" element={<HelpCenterPage />} />

          {/* Features Page — Public, no auth required */}
          <Route path="/features" element={<FeaturesPage />} />

          {/* Company Pages — Public, no auth required */}
          <Route path="/company" element={<CompanyPage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/careers" element={<CareersPage />} />
          <Route path="/contact" element={<ContactPage />} />

          {/* Legal Pages — Public, no auth required */}
          <Route path="/legal/privacy-policy" element={<PrivacyPolicyPage />} />
          <Route path="/legal/terms-of-service" element={<TermsOfServicePage />} />
          <Route path="/legal/cookie-policy" element={<CookiePolicyPage />} />

          {/* ============================================
              PERSISTENT EXPLORE LAYOUT - Twitter/Reddit Pattern
              ============================================
              CRITICAL: This is ONE parent Route element.
              Both /explore and /explore/:doi are CHILDREN.
              This ensures the SAME PersistentExploreLayout instance
              is preserved when navigating between list and detail.
              The explore list NEVER unmounts, scroll position is preserved.
          */}
          <Route element={<PersistentExploreLayout />}>
            {/* Explore list - shows ExplorePapers, Outlet renders null */}
            <Route path="/explore" element={null} />
            {/* Paper detail - shows detail, ExplorePapers hidden but NOT unmounted */}
            <Route path="/explore/:doi" element={<PaperDetail />} />
          </Route>

          {/* ============================================
              PERSISTENT ROOMS LAYOUT - Twitter/Reddit Pattern
              ============================================
              CRITICAL: This is ONE parent Route element.
              All room pages are CHILDREN routes.
              This ensures the SAME PersistentRoomsLayout instance
              is preserved when navigating between list and detail/preview.
              The rooms list NEVER unmounts, scroll position is preserved.
          */}
          <Route
            element={
              <ProtectedRoute>
                <PersistentRoomsLayout />
              </ProtectedRoute>
            }
          >
            {/* Labs list - shows ResearchRoomsList, Outlet renders null */}
            <Route path="/labs" element={null} />
            <Route path="/labs/my-labs" element={null} />
            {/* Lab Preview - list hidden, preview shown */}
            <Route path="/labs/:roomId/preview" element={<RoomPreviewPage />} />
            {/* Lab Detail - list hidden, detail shown */}
            <Route path="/labs/:roomId" element={<RoomDetail />} />
            <Route path="/labs/:roomId/:tab" element={<RoomDetail />} />
          </Route>
          {/* OLD Routes - Redirect to new structure for backward compatibility */}
          <Route
            path="/fields"
            element={
              <ProtectedRoute>
                <Navigate to="/labs" replace />
              </ProtectedRoute>
            }
          />
          <Route
            path="/fields/my-rooms"
            element={
              <ProtectedRoute>
                <Navigate to="/labs/my-labs" replace />
              </ProtectedRoute>
            }
          />
          {/* Redirect old /rooms URLs to /labs for backward compatibility */}
          <Route
            path="/rooms"
            element={
              <ProtectedRoute>
                <Navigate to="/labs" replace />
              </ProtectedRoute>
            }
          />
          <Route
            path="/rooms/my-rooms"
            element={
              <ProtectedRoute>
                <Navigate to="/labs/my-labs" replace />
              </ProtectedRoute>
            }
          />
          <Route
            path="/rooms/:roomId/preview"
            element={
              <ProtectedRoute>
                <RoomsToLabsRedirect preview />
              </ProtectedRoute>
            }
          />
          <Route
            path="/rooms/:roomId/*"
            element={
              <ProtectedRoute>
                <RoomsToLabsRedirect />
              </ProtectedRoute>
            }
          />
          <Route
            path="/chat/:roomId"
            element={
              <ProtectedRoute>
                <RoomChatPlaceholder />
              </ProtectedRoute>
            }
          />
          <Route
            path="/room-chat/:roomId"
            element={
              <ProtectedRoute>
                <RoomChatPlaceholder directAccess={true} />
              </ProtectedRoute>
            }
          />
          {/* Keep the /feed route as an alias to the home route */}
          <Route
            path="/feed"
            element={
              <ProtectedRoute>
                <Navigate to="/feed-home" replace />
              </ProtectedRoute>
            }
          />
          
          {/* ============================================
              PERSISTENT FEED LAYOUT - Twitter/Reddit Pattern
              ============================================
              CRITICAL: This is ONE parent Route element.
              Both /feed-home and /posts/:postId are CHILDREN.
              This ensures the SAME PersistentFeedLayout instance
              is preserved when navigating between feed and post detail.
              The feed NEVER unmounts, scroll position is preserved.
          */}
          <Route
            element={
              <ProtectedRoute>
                <PersistentFeedLayout />
              </ProtectedRoute>
            }
          >
            {/* Feed home - shows feed, Outlet renders null */}
            <Route path="/feed-home" element={null} />
            {/* Post detail - shows detail, feed hidden but NOT unmounted */}
            <Route path="/posts/:postId" element={<PostDetailPage />} />
          </Route>

          {/* Topics & Questions Routes */}
          <Route
            path="/topics/trending"
            element={
              <ProtectedRoute>
                <TrendingTopicsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/topics/:topic"
            element={
              <ProtectedRoute>
                <TopicPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/questions/unanswered"
            element={
              <ProtectedRoute>
                <UnansweredQuestionsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/collaborations"
            element={
              <ProtectedRoute>
                <BrowseOpportunities />
              </ProtectedRoute>
            }
          />
          <Route
            path="/questions/bounty"
            element={
              <ProtectedRoute>
                <BountyQuestionsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/drafts"
            element={
              <ProtectedRoute>
                <DraftsPage />
              </ProtectedRoute>
            }
          />

          {/* ============================================
              PERSISTENT COLLABORATORS LAYOUT - Twitter/Reddit Pattern
              ============================================
              This ensures the collaborators list NEVER unmounts when viewing profiles.
              Scroll position is preserved when navigating back.
          */}
          <Route
            path="/collaborators"
            element={
              <ProtectedRoute>
                <PersistentCollaboratorsLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={null} />
            <Route path="profile/:userId" element={<ProfessionalProfilePage />} />
          </Route>

          <Route
            path="/freelance/*"
            element={
              <ProtectedRoute>
                <FreelanceLayout />
              </ProtectedRoute>
            }
          />
          <Route
            path="/messages"
            element={
              <ProtectedRoute>
                <MessagingCenter />
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings/*"
            element={
              <ProtectedRoute>
                <SettingsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/profile/:userId?"
            element={
              <ProtectedRoute>
                <ProfessionalProfilePage />
              </ProtectedRoute>
            }
          />

          {/* ============================================
              PERSISTENT WORKSHOPS LAYOUT - Twitter/Reddit Pattern
              ============================================
              CRITICAL: This is ONE parent Route element.
              All workshop pages are handled by PersistentWorkshopsLayout.
              This ensures the SAME layout instance is preserved when 
              navigating between list and detail pages.
              The workshops list NEVER unmounts, scroll position is preserved.
              
              All views are rendered DIRECTLY in PersistentWorkshopsLayout:
              - /workshops              → Main list visible
              - /workshops/my-workshops → My Workshops visible, list hidden
              - /workshops/create       → Create form visible, list hidden
              - /workshops/:id          → Detail visible, list hidden
              - /workshops/:id/edit     → Edit form visible, list hidden
              - /workshops/:id/payment  → Payment page visible, list hidden
          */}
          <Route
            path="/workshops/*"
            element={
              <ProtectedRoute>
                <PersistentWorkshopsLayout />
              </ProtectedRoute>
            }
          />
          {/* Redirect for case-insensitive workshop URL */}
          <Route
            path="/Workshops/*"
            element={<Navigate to="/workshops" replace />}
          />

          {/* Courses Routes */}
          <Route
            path="/courses/*"
            element={
              <ProtectedRoute>
                <CoursesRoutes />
              </ProtectedRoute>
            }
          />

          {/* ============================================
              PERSISTENT COMMUNITIES LAYOUT - Twitter/Reddit Pattern
              ============================================
              CRITICAL: This is ONE parent Route element.
              All communities pages are CHILDREN routes.
              This ensures the SAME PersistentCommunitiesLayout instance
              is preserved when navigating between hub and detail pages.
              The communities hub NEVER unmounts, scroll position is preserved.
              
              All views are rendered DIRECTLY in PersistentCommunitiesLayout:
              - /communities → Hub visible (Feed/Discovery tabs)
              - /communities/:communityId/preview → Community preview
              - /communities/:communityId → Community dashboard
              - /communities/:communityId/threads/:threadId → Thread detail
              
              NO React Router Outlet is used - this prevents remounting.
          */}
          <Route
            path="/communities/*"
            element={
              <ProtectedRoute>
                <PersistentCommunitiesLayout />
              </ProtectedRoute>
            }
          />
        </Routes>
      </main>
    </div>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <UserProvider>
          <NotificationProvider>
            <RoomProvider>
              <RoomMessagesProvider>
                <RoomFilesProvider>
                  <RoomTasksProvider>
                    <RoomCalendarProvider>
                      <AppLayout />
                    </RoomCalendarProvider>
                  </RoomTasksProvider>
                </RoomFilesProvider>
              </RoomMessagesProvider>
            </RoomProvider>
          </NotificationProvider>
        </UserProvider>
      </BrowserRouter>
    </QueryClientProvider>
  );
}

export default App;

//COMMENT
